"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.radioDisabled = exports.radioHover = exports.radioFocus = exports.radioChecked = exports.radioUnchecked = exports.radioDefault = exports.radioReset = void 0;
exports.RadioGroupField = RadioGroupField;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_stately_1 = require("react-stately");
const components_1 = require("../components");
const HelperText_1 = require("../components/HelperText");
const Label_1 = require("../components/Label");
const Css_1 = require("../Css");
const ErrorMessage_1 = require("./ErrorMessage");
const defaultTestId_1 = require("../utils/defaultTestId");
const useTestIds_1 = require("../utils/useTestIds");
let nextNameId = 0;
/**
 * Provides a radio group with label.
 *
 * This is generally meant to be used in a form vs. being raw radio buttons.
 *
 * TODO: Add hover (non selected and selected) styles
 */
function RadioGroupField(props) {
    const { label, labelStyle, value, onChange, options, disabled = false, errorMsg, helperText, ...otherProps } = props;
    // useRadioGroupState uses a random group name, so use our name
    const name = (0, react_1.useMemo)(() => `radio-group-${++nextNameId}`, []);
    const state = (0, react_stately_1.useRadioGroupState)({
        name,
        value,
        onChange: (value) => onChange(value),
        isDisabled: disabled,
        isReadOnly: false,
    });
    const tid = (0, useTestIds_1.useTestIds)(props, (0, defaultTestId_1.defaultTestId)(label));
    // We use useRadioGroup b/c it does neat keyboard up/down stuff
    // TODO: Pass read only, required, error message to useRadioGroup
    const { labelProps, radioGroupProps } = (0, react_aria_1.useRadioGroup)({ label, isDisabled: disabled }, state);
    return (
    // default styling to position `<Label />` above.
    (0, jsx_runtime_1.jsxs)("div", { css: Css_1.Css.df.fdc.gap1.aifs.if(labelStyle === "left").fdr.gap2.jcsb.$, children: [(0, jsx_runtime_1.jsx)(Label_1.Label, { label: label, ...labelProps, ...tid.label, hidden: labelStyle === "hidden" }), (0, jsx_runtime_1.jsxs)("div", { ...radioGroupProps, children: [options.map((option) => {
                        const isDisabled = state.isDisabled || !!option.disabled;
                        return ((0, jsx_runtime_1.jsx)(react_1.Fragment, { children: (0, components_1.maybeTooltip)({
                                title: (0, components_1.resolveTooltip)(option.disabled),
                                placement: "bottom",
                                children: ((0, jsx_runtime_1.jsx)(Radio, { parentId: name, option: option, state: { ...state, isDisabled }, ...otherProps, ...tid[option.value] })),
                            }) }, option.value));
                    }), errorMsg && (0, jsx_runtime_1.jsx)(ErrorMessage_1.ErrorMessage, { errorMsg: errorMsg, ...tid.errorMsg }), helperText && (0, jsx_runtime_1.jsx)(HelperText_1.HelperText, { helperText: helperText })] })] }));
}
// Not meant to be standalone, but its own component so it can use hooks
function Radio(props) {
    const { parentId, option: { description, label, value }, state, ...others } = props;
    const disabled = state.isDisabled;
    const labelId = `${parentId}-${value}-label`;
    const descriptionId = `${parentId}-${value}-description`;
    const ref = (0, react_1.useRef)(null);
    const { inputProps } = (0, react_aria_1.useRadio)({ value, "aria-labelledby": labelId }, state, ref);
    const { focusProps, isFocusVisible } = (0, react_aria_1.useFocusRing)();
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)({ isDisabled: disabled });
    return ((0, jsx_runtime_1.jsxs)("label", { css: Css_1.Css.df.cursorPointer.mb1.if(disabled).add("cursor", "initial").$, ...hoverProps, children: [(0, jsx_runtime_1.jsx)("input", { type: "radio", ref: ref, css: {
                    ...exports.radioReset,
                    ...exports.radioDefault,
                    ...(!disabled && state.selectedValue === value ? exports.radioChecked : exports.radioUnchecked),
                    ...(disabled ? exports.radioDisabled : {}),
                    ...(isHovered ? exports.radioHover : {}),
                    ...(isFocusVisible ? exports.radioFocus : {}),
                    // Nudge down so the center of the circle lines up with the label text
                    ...Css_1.Css.mtPx(2).mr1.$,
                }, disabled: disabled, "aria-labelledby": labelId, ...inputProps, ...focusProps, ...others }), (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("div", { id: labelId, css: Css_1.Css.smMd.gray800.if(disabled).gray400.$, ...(description ? { "aria-describedby": descriptionId } : {}), children: label }), description && ((0, jsx_runtime_1.jsx)("div", { id: descriptionId, css: Css_1.Css.sm.gray700.if(disabled).gray400.$, children: typeof description === "function" ? description() : description }))] })] }));
}
const whiteCircle = "data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='8' cy='8' r='4'/%3e%3c/svg%3e";
// Didn't put these in radioReset yet, are they needed?
// color-adjust: exact;
// -webkit-print-color-adjust: exact;
exports.radioReset = {
    ...Css_1.Css.add("appearance", "none").p0.dib.vam.add("userSelect", "none").fs0.h2.w2.br100.$,
    ...Css_1.Css.add("outline", "0px solid transparent").$,
};
exports.radioDefault = {
    // By default we're a white circle with a gray border
    ...Css_1.Css.bgWhite.bcGray300.ba.$,
    // Set the "selected" color that will be used by background=currentColor + box shadow, but is initially ignored
    ...Css_1.Css.blue700.$,
    // Apply our default transitions
    ...Css_1.Css.transition.$,
};
// Unchecked means a gray border
exports.radioUnchecked = Css_1.Css.cursorPointer.bcGray300.$;
// Checked means a blue circle (achieved by a blue background + white dot background image)
exports.radioChecked = {
    // Make the background become the current (blue) color
    ...Css_1.Css.add("backgroundColor", "currentColor")
        .add("backgroundSize", "100% 100%")
        .add("backgroundPosition", "center")
        .add("backgroundRepeat", "no-repeat").$,
    // And use backgroundImage to draw a white dot in the middle of the background
    ...Css_1.Css.add("backgroundImage", `url("${whiteCircle}")`).$,
    // Make our border the same color as the dot
    ...Css_1.Css.add("borderColor", "currentColor").$,
};
// When active draw another circle via boxShadow
exports.radioFocus = {
    // The box shadow ends up drawing over this afaict?
    ...Css_1.Css.add("outline", "2px solid transparent").add("outlineOffset", "2px").$,
    // Draw 1st box shadow of white/outline, 2nd box current (blue) of another outline
    ...Css_1.Css.bshFocus.$,
};
exports.radioHover = {
    // Change both the dot and the border to a darker blue
    ...Css_1.Css.blue900.bcBlue900.$,
};
exports.radioDisabled = {
    ...Css_1.Css.cursorNotAllowed.gray100.$,
    ...Css_1.Css.add("backgroundColor", "currentColor")
        .add("backgroundSize", "100% 100%")
        .add("backgroundPosition", "center")
        .add("backgroundRepeat", "no-repeat").$,
};
